import { useState, useEffect } from "react";
import moment from "moment";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { useLoading } from "../../../shared/context/LoadingContext";
import { Link } from "react-router-dom";
//MUI
import { DataGrid } from "@mui/x-data-grid";
import SendIcon from "@mui/icons-material/Send";
import SendAndArchiveIcon from "@mui/icons-material/SendAndArchive";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useContingenciaEfatura } from "../../../shared/hooks/contingencia-efatura-hook";
import Tooltip from "@mui/material/Tooltip";
import "./ContingenciaDocumentos.scss";

function ContingenciaDocumentos({ clinicaId, auth }) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();

  const [documentos, setDocumentos] = useState([]);
  const [rows, setRows] = useState([]);

  const [errosTransmissao, setErrosTransmissao] = useState(null);

  useEffect(() => {
    fetchDocumentosContingencia();
  }, []);

  const fetchDocumentosContingencia = async () => {
    startLoading();
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/contingencia/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );

      setDocumentos(response.documentos);
      formatRows(response.documentos);
    } catch (err) {
      console.error("Erro ao buscar documentos em contingência:", err);
    } finally {
      stopLoading();
    }
  };

  const formatRows = (docs) => {
    const formattedRows = docs.map((doc) => {
      /* O nome vem de três sítios; o ID do paciente, só de dois.
       *
       *   contaCorrente.cliente     → paciente ✔
       *   entrada.cliente           → paciente ✔ (reembolsos)
       *   entrada.clienteFornecedor → NÃO é paciente: é outra coleção, sem
       *                               ficha em /clientes
       *
       * Antes o `idCliente` só era lido do primeiro, e o link era desenhado
       * conforme o TIPO do documento. Um RCE ou NCE de uma entrada não é FTE,
       * logo levava link — mas uma entrada não tem contaCorrente, e o link
       * saía `/clientes/undefined`.
       *
       * A pergunta certa não é «que tipo de documento é», é «esta linha tem
       * um paciente?». */
      let clienteNome = "-";
      let idCliente = null;
      if (doc.contaCorrente && doc.contaCorrente.cliente) {
        clienteNome =
          doc.contaCorrente.cliente.nomeFiscal ||
          doc.contaCorrente.cliente.nome;
        idCliente = doc.contaCorrente.cliente._id;
      } else if (doc.entrada && doc.entrada.clienteFornecedor) {
        clienteNome = doc.entrada.clienteFornecedor.nome;
      } else if (doc.entrada && doc.entrada.cliente) {
        clienteNome =
          doc.entrada.cliente.nomeFiscal || doc.entrada.cliente.nome;
        idCliente = doc.entrada.cliente._id;
      }
      return {
        id: doc._id,
        tipoDocumento: doc.tipoDocumento || "-",
        nrDocumento: doc.nrDocumento || "-",
        serie: doc.serie || "-",
        dataEmissao: doc.dataEmissaoContingencia || doc.created_at,
        motivoContingencia: doc.motivoContingencia || "Não especificado",
        valor: doc.valor || 0,
        statusEmissao: doc.transmitidoAposContingencia ? "Emitido" : "Pendente",
        cliente: clienteNome,
        idCliente,
      };
    });

    setRows(formattedRows);
  };

  const emitirDocumento = async (documentoId) => {
    startLoading();
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/emitir-contingencia/${documentoId}`,
        "POST",
        JSON.stringify({ usuarioTransmissao: auth.userId }),
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );

      if (response.success === false) {
        setErrosTransmissao({
          documentoNr: `${response.documento?.tipoDocumento || ""}/${
            response.documento?.nrDocumento || ""
          }`,
          mensagens: response.erros || [],
        });
      }

      // Atualizar lista após emissão
      fetchDocumentosContingencia();
    } catch (err) {
      console.error("Erro ao emitir documento:", err);
    } finally {
      stopLoading();
    }
  };

  const emitirTodosDocumentos = async () => {
    startLoading();
    try {
      const documentosParaEmitir = documentos
        .filter((doc) => !doc.transmitidoAposContingencia)
        .map((doc) => doc._id);

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/emitir-contingencia/lote/${clinicaId}`,
        "POST",
        JSON.stringify({
          documentoIds: documentosParaEmitir,
          usuarioTransmissao: auth.userId,
        }),
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );

      if (response.resultados?.falhas > 0) {
        const detalheErro = response.resultados.detalhes.find(
          (d) => d.status === "erro",
        );

        if (detalheErro) {
          setErrosTransmissao({
            documentoNr: `${detalheErro.tipoDocumento || ""}/${
              detalheErro.nrDocumento || ""
            }`,
            mensagens: detalheErro.mensagensEfatura || [
              { description: detalheErro.erro },
            ],
            sucessosAntes: response.resultados.sucessos,
          });
        }
      }

      // Atualizar lista após emissão
      fetchDocumentosContingencia();
    } catch (err) {
      console.error("Erro ao emitir documentos em lote:", err);
    } finally {
      stopLoading();
    }
  };

  const documentoMaisAntigoPendenteId = documentos
    .filter((doc) => !doc.transmitidoAposContingencia)
    .sort(
      (a, b) =>
        new Date(a.dataEmissaoContingencia || a.created_at) -
        new Date(b.dataEmissaoContingencia || b.created_at),
    )[0]?._id;

  const actionColumn = [
    {
      field: "status",
      headerName: "Estado",
      width: 80,
      renderCell: (params) => {
        return (
          <div>
            {params.row.statusEmissao === "Pendente" ? (
              <WarningAmberIcon style={{ color: "#ff6b00" }} />
            ) : params.row.statusEmissao === "Emitido" ? (
              <CheckCircleIcon style={{ color: "green" }} />
            ) : (
              <ErrorIcon style={{ color: "crimson" }} />
            )}
          </div>
        );
      },
    },
    {
      field: "tipoDocumento",
      headerName: "Tipo",
      width: 100,
      renderCell: (params) => {
        return <span>{params.row.tipoDocumento}</span>;
      },
    },
    {
      field: "nrDocumento",
      headerName: "Nº Documento",
      flex: 1,
      renderCell: (params) => {
        return (
          <span>
            {params.row.tipoDocumento}/{params.row.serie}/
            {params.row.nrDocumento}
          </span>
        );
      },
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 2,
      renderCell: (params) => {
        // Só há link se houver ficha para onde ir. Ver a nota no `formatRows`.
        return params.row.idCliente ? (
          <Link
            to={`/clientes/${params.row.idCliente}`}
            className="link-cliente-receita"
          >
            {params.row.cliente}
          </Link>
        ) : (
          params.row.cliente
        );
      },
    },
    {
      field: "dataEmissao",
      headerName: "Data Emissão",
      flex: 1,
      renderCell: (params) => {
        return (
          <span>
            {moment(params.row.dataEmissao).format("DD/MM/YYYY HH:mm")}
          </span>
        );
      },
    },
    {
      field: "motivoContingencia",
      headerName: "Motivo",
      flex: 2,
    },
    {
      field: "valor",
      headerName: "Valor",
      flex: 1,
      renderCell: (params) => {
        return <span>{params.row.valor?.toLocaleString("pt-BR")}$00</span>;
      },
    },
    {
      field: "statusEmissao",
      headerName: "Estado",
      flex: 1,
      renderCell: (params) => {
        return (
          <span
            className={
              params.row.statusEmissao === "Pendente"
                ? "status-pendente"
                : params.row.statusEmissao === "Emitido"
                  ? "status-emitido"
                  : "status-erro"
            }
          >
            {params.row.statusEmissao}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Ações",
      flex: 1,
      renderCell: (params) => {
        return (
          <div>
            {params.row.statusEmissao === "Pendente" &&
              params.row.id === documentoMaisAntigoPendenteId && (
                <button
                  className="btn-emitir-documento"
                  onClick={() => emitirDocumento(params.row.id)}
                >
                  <SendIcon style={{ fontSize: "18px" }} />
                  <span>Emitir</span>
                </button>
              )}
          </div>
        );
      },
    },
  ];

  const documentosPendentes = documentos.filter(
    (doc) => !doc.transmitidoAposContingencia,
  ).length;

  //contigencia
  const {
    modoContingenciaAtivo,
    ativarModoContingencia,
    desativarModoContingencia,
    getTempoRestante,
  } = useContingenciaEfatura();

  const handleToggleContingencia = () => {
    if (modoContingenciaAtivo) {
      desativarModoContingencia();
    } else {
      ativarModoContingencia(5, ""); // ReasonTypeCode 5 (problemas técnicos)
    }
  };

  return (
    <div className="contingencia-documentos">
      <div className="contingencia-documentos__header">
        {(auth.role === "Médico/Administrador" ||
          auth.role === "Gerente" ||
          auth.role === "Secretária(o)") && (
          <div className="item contingencia-efatura-toggle">
            <Tooltip
              title={
                modoContingenciaAtivo
                  ? `Modo Contingência Ativo ${
                      getTempoRestante() ? `(${getTempoRestante().texto})` : ""
                    }`
                  : "Ativar Modo Contingência E-fatura"
              }
            >
              <div
                className="checkbox-wrapper-51"
                id="planoTratamento__mostrar-terminados"
              >
                <input
                  type="checkbox"
                  id="mostrarFinalizados"
                  onChange={handleToggleContingencia}
                  checked={modoContingenciaAtivo}
                />
                <label htmlFor="mostrarFinalizados" className="toggle">
                  <span>
                    <svg width="10px" height="10px" viewBox="0 0 10 10">
                      <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                    </svg>
                  </span>
                </label>

                <span className="checkbox-text contingenciaCheckbox">
                  Contingência
                </span>
              </div>
            </Tooltip>
          </div>
        )}
        <div className="contingencia-documentos__header__info">
          <WarningAmberIcon className="contingencia-documentos__header__icon" />
          <div>
            <h2>Documentos Emitidos em Contingência</h2>
            <p>
              {documentosPendentes > 0
                ? `${documentosPendentes} documento(s) pendente(s) de emissão`
                : "Todos os documentos foram emitidos"}
            </p>
          </div>
        </div>

        {documentosPendentes > 0 && (
          <button className="btn-emitir-todos" onClick={emitirTodosDocumentos}>
            <SendAndArchiveIcon />
            <span>Emitir Todos ({documentosPendentes})</span>
          </button>
        )}
      </div>

      {errosTransmissao && (
        <div className="contingencia-documentos__erros">
          <div className="contingencia-documentos__erros__header">
            <ErrorIcon style={{ color: "crimson", fontSize: "24px" }} />
            <div>
              <strong>
                Erro ao transmitir documento {errosTransmissao.documentoNr}
              </strong>
              {errosTransmissao.sucessosAntes > 0 && (
                <p
                  style={{ margin: "4px 0 0", fontSize: "13px", color: "#666" }}
                >
                  {errosTransmissao.sucessosAntes} documento(s) emitido(s) com
                  sucesso antes do erro.
                </p>
              )}
            </div>
            <button
              className="contingencia-documentos__erros__fechar"
              onClick={() => setErrosTransmissao(null)}
            >
              ✕
            </button>
          </div>
          <div className="contingencia-documentos__erros__lista">
            {errosTransmissao.mensagens.map((msg, index) => (
              <div
                key={index}
                className="contingencia-documentos__erros__mensagem"
              >
                {msg.code && (
                  <span className="contingencia-documentos__erros__codigo">
                    {msg.code}
                  </span>
                )}
                <span>{msg.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="contingencia-documentos__grid">
        {rows && rows.length > 0 ? (
          <DataGrid
            rows={rows}
            columns={actionColumn}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(row) => row.id}
          />
        ) : (
          <div className="contingencia-documentos__empty">
            <CheckCircleIcon style={{ fontSize: "64px", color: "#ccc" }} />
            <p>Nenhum documento em contingência encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContingenciaDocumentos;
